import React, { ChangeEvent } from 'react';

interface MaskedInputProps {
    value: string;
    onChange: (value: string) => void;
    mask: 'cpf' | 'cnpj' | 'phone' | 'cep';
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    required?: boolean;
}

interface MaskConfig {
    maxLength: number;
    pattern: RegExp;
    format: (value: string) => string;
    placeholder: string;
}

const MaskedInput: React.FC<MaskedInputProps> = ({
    value,
    onChange,
    mask,
    placeholder,
    disabled,
    className = '',
    required = false
}) => {
    // Configurações das máscaras
    const maskConfigs: Record<string, MaskConfig> = {
        cpf: {
            maxLength: 14, // 000.000.000-00
            pattern: /^(\d{0,3})(\d{0,3})(\d{0,3})(\d{0,2})$/,
            format: (value: string) => {
                return value.replace(
                    /^(\d{0,3})(\d{0,3})(\d{0,3})(\d{0,2})/,
                    (_, g1, g2, g3, g4) => {
                        if (g4) return `${g1}.${g2}.${g3}-${g4}`;
                        if (g3) return `${g1}.${g2}.${g3}`;
                        if (g2) return `${g1}.${g2}`;
                        return g1;
                    }
                );
            },
            placeholder: '000.000.000-00'
        },
        cnpj: {
            maxLength: 18, // 00.000.000/0000-00
            pattern: /^(\d{0,2})(\d{0,3})(\d{0,3})(\d{0,4})(\d{0,2})$/,
            format: (value: string) => {
                return value.replace(
                    /^(\d{0,2})(\d{0,3})(\d{0,3})(\d{0,4})(\d{0,2})/,
                    (_, g1, g2, g3, g4, g5) => {
                        if (g5) return `${g1}.${g2}.${g3}/${g4}-${g5}`;
                        if (g4) return `${g1}.${g2}.${g3}/${g4}`;
                        if (g3) return `${g1}.${g2}.${g3}`;
                        if (g2) return `${g1}.${g2}`;
                        return g1;
                    }
                );
            },
            placeholder: '00.000.000/0000-00'
        },
        phone: {
            maxLength: 15, // (00) 00000-0000
            pattern: /^(\d{0,2})(\d{0,5})(\d{0,4})$/,
            format: (value: string) => {
                return value.replace(
                    /^(\d{0,2})(\d{0,5})(\d{0,4})/,
                    (_, g1, g2, g3) => {
                        if (g3) return `(${g1}) ${g2}-${g3}`;
                        if (g2) return `(${g1}) ${g2}`;
                        if (g1) return `(${g1}`;
                        return '';
                    }
                );
            },
            placeholder: '(00) 00000-0000'
        },
        cep: {
            maxLength: 9, // 00000-000
            pattern: /^(\d{0,5})(\d{0,3})$/,
            format: (value: string) => {
                return value.replace(
                    /^(\d{0,5})(\d{0,3})/,
                    (_, g1, g2) => {
                        if (g2) return `${g1}-${g2}`;
                        return g1;
                    }
                );
            },
            placeholder: '00000-000'
        }
    };

    const config = maskConfigs[mask];

    const applyMask = (value: string): string => {
        // Remove todos os caracteres não numéricos
        const numbers = value.replace(/\D/g, '');

        // Limita o número de caracteres numéricos baseado na máscara
        const maxDigits = config.maxLength - (config.maxLength - config.placeholder.replace(/\D/g, '').length);
        const limitedNumbers = numbers.slice(0, maxDigits);

        // Aplica a formatação
        return config.format(limitedNumbers);
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const newValue = applyMask(e.target.value);
        onChange(newValue);
    };

    // Validação do comprimento exato esperado para cada tipo
    const isValid = (): boolean => {
        const numbers = value.replace(/\D/g, '');
        switch (mask) {
            case 'cpf':
                return numbers.length === 11;
            case 'cnpj':
                return numbers.length === 14;
            case 'phone':
                return numbers.length >= 10 && numbers.length <= 11;
            case 'cep':
                return numbers.length === 8;
            default:
                return true;
        }
    };

    // Helper para mostrar o comprimento atual vs máximo
    const getCurrentLength = (): string => {
        const numbers = value.replace(/\D/g, '');
        const maxDigits = config.placeholder.replace(/\D/g, '').length;
        return `${numbers.length}/${maxDigits}`;
    };

    return (
        <div className="relative">
            <input
                type="text"
                value={value}
                onChange={handleChange}
                placeholder={placeholder || config.placeholder}
                disabled={disabled}
                className={`form-control ${className} ${!isValid() && value ? 'border-red-500' : ''}`}
                required={required}
                maxLength={config.maxLength}
            />
            
        </div>
    );
};

export default MaskedInput;